import { useEffect, useMemo, useRef, useState } from 'react';

import styles from '../ScenarioGenerator.module.css';
import { HelpTooltip } from '../../../common/HelpTooltip';
import { PromptHistoryModal } from '../../../common/PromptHistoryModal';
import { PROMPT_HISTORY_KEYS } from '../../../../utils/promptHistory';
import { ConfirmDialog } from '../../../common/ConfirmDialog';
import { useGenerationModels } from '../../../../features/generation-models/hooks';
import { GenerateButton } from '../../common/GenerateButton';
import { VideoGenerationSettings } from '../VideoGenerationSettings';
import { VideoFrameModeSelector } from '../VideoFrameModeSelector';
import { PromptVideoDisplay } from '../PromptVideoDisplay';
import { VideoJobHistoryPanel } from '../VideoJobHistoryPanel';
import { MaterialMentionPopup } from '../MaterialMentionPopup';
import { MentionTokenChipOverlay } from '../MentionTokenChipOverlay';
import { useMaterialMention } from '../hooks/useMaterialMention';
import { useMentionTokenChips } from '../hooks/useMentionTokenChips';
import type { SetupPanelProps } from '../SetupPanel';
import { useScenarioSetup } from '../hooks/ScenarioSetupContext';
import { useGenerationAssets } from '../../state/assetsContext';
import { getMotionLayeredAssetNotice, motionLayeredCanvasAspectNotice } from '../../../../features/motion/layeredPromptMode';
import { findMotionLayeredTarget } from '../../../../features/motion/layeredGenerate';
import { useMotionGraphicsTabState } from '../../../../features/motion/useMotionGraphicsTabVisible';
import { motionLayeredUnavailableMessage } from '../../../../features/motion/availability';
import { useMotionLayeredSettingsStore } from '../../../../features/motion/layeredSettingsStore';
import { MotionGraphicsSettingsSection } from './MotionGraphicsSettingsSection';
import { isCheckedLayerStack, layerMaterialDisplayName, parseLayerStack } from '../../../../features/motion/layerStack';
import { useMotionLayerEditsStore } from '../../../../features/motion/layerEditsStore';
import { useMotionLayerModalStore } from '../../../../features/motion/layerModalStore';
import { MotionLayerModal } from '../../assets/MotionLayerModal';

/**
 * BE のエラーレスポンスから「カタログ起因の参照音声の利用不可」を判別する
 * (docs/features/reference-audio-roles-plan.md §2.7・フェーズ5b)。
 *
 * バックエンド (routes/async_jobs/video.py) は運営提供SEカタログの素材が
 * 存在しない/提供終了 (is_active=false) のときのみ error_code に
 * 'REFERENCE_AUDIO_CATALOG_UNAVAILABLE' + catalog_item_id を付与する
 * (それ以外の参照音声エラーは従来どおり REFERENCE_AUDIO_VALIDATION_FAILED)。
 * この2つを区別できないと「無言で除いて続行」になってしまうため (§2.7 で禁止)、
 * catalog_item_id の有無で確認ダイアログの要否を判定する。
 */
function extractCatalogAudioUnavailable(
  error: unknown,
): { catalogItemId: string; message: string } | null {
  const data = (error as { response?: { data?: {
    error_code?: string; error?: string; catalog_item_id?: string;
  } } } | null | undefined)?.response?.data;
  if (data?.error_code !== 'REFERENCE_AUDIO_CATALOG_UNAVAILABLE') return null;
  const catalogItemId = data.catalog_item_id;
  if (!catalogItemId) return null;
  return { catalogItemId, message: data.error || 'この参照音声は利用できません。' };
}

export function PromptModePanel(props: SetupPanelProps) {
  const {
    accordions,
    videoGenerationMutation,
    selectedAssets,
    mentionEligibleAssets,
    mentionActorSources,
    promptDisabled,
    handleVideoGenerationModeChange,
    promptTextareaRef,
    isVideoCreditInsufficient,
    handleGenerateVideoFromPrompt,
    motionLayeredPrompt,
    promptVideoGenerated, promptVideoMaterialId, promptVideoDisplayRef,
    tempMaterials, cloudMaterials, setPreviewModalMaterial,
    handleVideoHistoryPreview, handleRestoreVideoSettings,
    openPurchaseModal, resolveErrorMessage,
    showVideoPromptHistory, setShowVideoPromptHistory, handleSelectVideoPromptHistory,
    isStorageExceeded } = props;

  const {
    mode,
    videoGenerationMode, isReferenceImageMode,
    videoSettingsSummaryPrompt,
    videoSettings, setVideoSettings,
    videoPrompt, setVideoPrompt,
    estimatedVideoCredits,
    aggregatesReferenceVideoSeconds, referenceVideoSecondsTotal, hasCeiledReferenceVideoDuration } = useScenarioSetup();

  const {
    isPromptSectionOpen, setIsPromptSectionOpen,
    isPromptVideoSettingsOpen, setIsPromptVideoSettingsOpen } = accordions;

  // SEカタログ由来の参照音声が提供終了/削除された状態で生成 (再生成) しようとしたときの
  // 確認ダイアログ (docs/features/reference-audio-roles-plan.md §2.7)。「無言で除いて
  // 続行」は禁止のため、承認したときだけ該当音声を除いて再送する。この画面には
  // 「履歴を丸ごと復元して再送する」専用フローが無く、選択中の素材のまま生成ボタンを
  // 押し直す動線しか無いため、直近の送信ペイロード (videoGenerationMutation.variables)
  // を起点に再送する。
  const { removeAsset } = useGenerationAssets();
  const catalogAudioUnavailable = videoGenerationMutation.isError
    ? extractCatalogAudioUnavailable(videoGenerationMutation.error)
    : null;
  // 同じエラーに対してダイアログを閉じた後、mutation の isError が非同期に切り替わるまでの
  // 一瞬の再表示チラつきを防ぐための dismiss 済みID。
  const [catalogAudioDialogDismissedFor, setCatalogAudioDialogDismissedFor] = useState<string | null>(null);
  const showCatalogAudioDialog = !!catalogAudioUnavailable
    && catalogAudioUnavailable.catalogItemId !== catalogAudioDialogDismissedFor;

  const handleCatalogAudioUnavailableCancel = () => {
    if (!catalogAudioUnavailable) return;
    setCatalogAudioDialogDismissedFor(catalogAudioUnavailable.catalogItemId);
    // 再生成を中止する。エラー状態も畳んで「もう一度生成」を押し直せる状態に戻す
    // (§2.7: キャンセル時は音声の再選択を促す。選択中のチップはそのまま残すので
    // ユーザーが用途ドロップダウン付近から外す/差し替える)。
    videoGenerationMutation.reset();
  };

  const handleCatalogAudioUnavailableRetry = () => {
    if (!catalogAudioUnavailable) return;
    const { catalogItemId } = catalogAudioUnavailable;
    const lastPayload = videoGenerationMutation.variables as Record<string, unknown> | undefined;
    setCatalogAudioDialogDismissedFor(catalogItemId);
    // 選択中素材からも外す (次回の生成でまた同じエラーを踏まないため)。
    removeAsset(catalogItemId);
    if (!lastPayload) return;
    const rawInputs = Array.isArray(lastPayload.reference_audio_inputs)
      ? lastPayload.reference_audio_inputs as Array<Record<string, unknown>>
      : [];
    const filteredInputs = rawInputs.filter((input) => {
      const source = input.source as { kind?: string; id?: string } | undefined;
      return !(source?.kind === 'audio_catalog' && source.id === catalogItemId);
    });
    videoGenerationMutation.mutate({ ...lastPayload, reference_audio_inputs: filteredInputs });
  };

  // 素材 @メンション (docs/features/material-at-mention-plan.md P2/「俳優メンション」節)。
  // 動画プロンプト入力欄の textarea に接続する。候補は mentionEligibleAssets
  // (selectedAssets のうち isMentionableReferenceAsset を満たし、かつ動画生成の
  // 参照上限で実際に送信される素材。レビュー指摘 P1) + mentionActorSources
  // (選択中の AI俳優。casting/seedancePreset は排他で常に候補) を対象にする
  // (useMaterialMention 内部で isMentionableReferenceAsset の絞り込みを行う)。
  const mentionPopupRef = useRef<HTMLDivElement>(null);
  const mention = useMaterialMention({
    textareaRef: promptTextareaRef,
    value: videoPrompt,
    onChange: setVideoPrompt,
    assets: mentionEligibleAssets,
    actors: mentionActorSources,
    popupRef: mentionPopupRef,
  });
  // トークンのチップ表示 (オーバーレイ装飾方式・docs/features/material-at-mention-plan.md
  // 「トークンのチップ表示」節)。本文は @A#3f9c のまま変えず、見た目だけ重ねる。
  const promptMentionChips = useMentionTokenChips({
    textareaRef: promptTextareaRef,
    value: videoPrompt,
    assets: mentionEligibleAssets,
    actors: mentionActorSources,
  });

  // 代表となる参照動画 (先頭)。出力の解像度/アスペクト比/音声をこれに連動させる (v2v)。
  // フレーム指定 (videoReferenceMode='first_frame'/'last_frame') の動画は参考画像として
  // 送られるため連動対象から外す。
  const isVideoModeRefAsset = (asset: (typeof selectedAssets)[number]) =>
    asset.usage === 'reference_video' && (asset.videoReferenceMode ?? 'video') === 'video';
  const refVideoAsset = selectedAssets.find(isVideoModeRefAsset);

  // 尺「自動」(-1) の見積もりが仮定している最大秒数 (注記の表示用)。
  // useEstimatedVideoCredits と同じ解決: モデルの supported_durations の正の最大値
  // (2.5=30 / 2.0系=15。backend の normalize_billing_duration のみなし秒数と一致)。
  const { data: videoModels = [] } = useGenerationModels('video');
  // 3 タブ目「モーショングラフィックス」(docs/features/motion-graphics-mode-plan.md §5.1)。
  // 表示はブランドのフラグ かつ 暫定ゲートを通るときだけ (判定は useMotionGraphicsTabState。
  // ScenarioGenerator の自動生成・GenerationWorkspace の 2 件目拒否と共用)。取得中 (pending) は
  // タブを出さないが、motion を reference へ寄せもしない (結果を待つ)。
  const motionTab = useMotionGraphicsTabState();
  const motionTabUndecided = mode === 'prompt' && videoGenerationMode === 'motion' && motionTab.pending;
  const showMotionTab = mode === 'prompt' && motionTab.visible;
  const isMotionMode = videoGenerationMode === 'motion' && showMotionTab;
  // 設定値は store (生成の body に載せる useVideoHandlers と共有)
  const motionSettings = useMotionLayeredSettingsStore((s) => s.settings);
  const setMotionSettings = useMotionLayeredSettingsStore((s) => s.setSettings);
  const motionUnavailableMessage = motionLayeredUnavailableMessage(motionLayeredPrompt.availability);
  const motionCredits = motionLayeredPrompt.availability?.enabled ? motionLayeredPrompt.availability.credits : null;
  const motionReadiness = motionLayeredPrompt.readiness;
  const motionAssetNotice = isMotionMode ? getMotionLayeredAssetNotice(selectedAssets) : null;
  // レイヤー素材 (motion の素材は 1 件なので最初の 1 件)。設定欄の「レイヤー素材」行とモーダルの対象
  const motionLayerTarget = useMemo(
    () => (isMotionMode ? findMotionLayeredTarget(selectedAssets) : null),
    [isMotionMode, selectedAssets],
  );
  // レイヤー素材が選ばれた (素材 id が変わった) ら、比率をカンバスに最も近いものにする (§5.3)。
  // 同じ素材のままなら利用者が変えた比率を保つ (store が適用済みの素材 id を覚える)
  const applyCanvasAspect = useMotionLayeredSettingsStore((s) => s.applyCanvasAspect);
  const motionLayerTargetId = motionLayerTarget?.asset.id ?? null;
  const motionLayerCanvas = motionLayerTarget?.stack.canvas ?? null;
  useEffect(() => {
    if (motionLayerTargetId && motionLayerCanvas) applyCanvasAspect(motionLayerTargetId, motionLayerCanvas);
  }, [motionLayerTargetId, motionLayerCanvas, applyCanvasAspect]);
  const motionAspectNotice = motionLayeredCanvasAspectNotice(motionSettings.aspectRatio, motionLayerCanvas);
  const motionLockedCount = useMotionLayerEditsStore(
    (s) => (motionLayerTarget ? (s.locked[motionLayerTarget.asset.id] ?? []).length : 0),
  );
  // モーダルの開閉は layerModalStore (選択済み素材チップのバッジからも開くため)。
  // 開いている素材が選択から外れたらモーダルは描かない
  const openLayerMaterialId = useMotionLayerModalStore((s) => s.openMaterialId);
  const openLayerModal = useMotionLayerModalStore((s) => s.openLayerModal);
  const closeLayerModal = useMotionLayerModalStore((s) => s.closeLayerModal);
  const layerModalTarget = useMemo(() => {
    if (!isMotionMode || !openLayerMaterialId) return null;
    const asset = selectedAssets.find((a) => a.id === openLayerMaterialId);
    const stack = asset ? parseLayerStack(asset.layerStack) : null;
    return asset && stack && isCheckedLayerStack(stack) ? { asset, stack } : null;
  }, [isMotionMode, openLayerMaterialId, selectedAssets]);

  const assumedMaxDurationSeconds = useMemo(() => {
    const model = videoModels.find((m) => m.model_code === videoSettings.videoModel);
    const positives = (model?.supported_durations ?? [])
      .filter((d): d is number => typeof d === 'number' && d > 0);
    return positives.length > 0 ? Math.max(...positives) : 15;
  }, [videoModels, videoSettings.videoModel]);

  return (
    <>
            {/* 動画生成モード選択（常に表示、区切り線なし） */}
            <VideoFrameModeSelector
              mode={videoGenerationMode}
              onModeChange={handleVideoGenerationModeChange}
              disabled={videoGenerationMutation.isPending}
              showBorders={false}
              isLiteModel={videoSettings.videoModel === 'veo-3.1-lite'}
              isPromptMode={mode === 'prompt'}
              showMotionTab={showMotionTab}
              motionTabPending={mode === 'prompt' && motionTab.pending}
            />

            {/* プロンプトアコーディオン（デフォルトオープン） */}
            <div className={`accordion-section ${isPromptSectionOpen ? 'accordion-section-open' : ''}`}>
              <button
                type="button"
                className="accordion-header"
                onClick={() => setIsPromptSectionOpen(!isPromptSectionOpen)}
                aria-expanded={isPromptSectionOpen}
              >
                <span className="accordion-title">
                  <img src="/content_pen_write.svg" alt="" className="accordion-title-icon" />
                  <span>{isMotionMode ? '動きの指示' : 'プロンプト'}</span>
                </span>
                <span className={`accordion-icon ${isPromptSectionOpen ? 'accordion-icon-open' : ''}`}>
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" style={{ transition: 'transform 0.2s', transform: isPromptSectionOpen ? 'rotate(90deg)' : 'none' }}><path d="M2 0l4 4-4 4z" /></svg>
                </span>
              </button>
              <div className={`accordion-panel ${isPromptSectionOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
                <div className="prompt-history-container">
                  <div className="prompt-label-row">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span className="gen-label-text">{isMotionMode ? '動きの指示' : 'プロンプト'}</span>
                      {/* 動画プロンプトのヘルプはモーショングラフィックスには当てはまらないので出さない */}
                      {!isMotionMode && (
                        <HelpTooltip contentKey="promptVideo" ariaLabel="プロンプトのヘルプ" placement="bottom" />
                      )}
                    </span>
                    <button
                      type="button"
                      className="prompt-history-toggle"
                      onClick={() => setShowVideoPromptHistory(!showVideoPromptHistory)}
                    >
                      <span aria-hidden="true" style={{ display: 'inline-block', width: 12, height: 12, marginRight: 4, verticalAlign: '-2px', backgroundColor: 'currentColor', WebkitMaskImage: 'url(/time_clock_circle.svg)', maskImage: 'url(/time_clock_circle.svg)', WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat', WebkitMaskPosition: 'center', maskPosition: 'center' }} />
                      履歴
                    </button>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <textarea
                      ref={promptTextareaRef}
                      value={videoPrompt}
                      rows={6}
                      placeholder={isMotionMode
                        ? '例: STEP 1 から順にテンポよく文字を読ませる。上部のロゴは動かさない'
                        : '動画生成用のプロンプトを入力してください（@で選択中の素材を参照できます）'}
                      disabled={promptDisabled}
                      className="gen-textarea"
                      {...mention.textareaHandlers}
                    />
                    <MentionTokenChipOverlay chips={promptMentionChips.chips} />
                  </div>
                  {mention.isOpen && (
                    <MaterialMentionPopup
                      ref={mentionPopupRef}
                      candidates={mention.candidates}
                      activeIndex={mention.activeIndex}
                      position={mention.popupPosition}
                      onSelect={mention.selectCandidate}
                      onHoverIndex={mention.setActiveIndex}
                    />
                  )}

                  {/* プロンプト履歴モーダル (全画面) */}
                  <PromptHistoryModal
                    isOpen={showVideoPromptHistory}
                    onClose={() => setShowVideoPromptHistory(false)}
                    scope="video_prompt"
                    storageKey={PROMPT_HISTORY_KEYS.VIDEO_GENERATION}
                    onSelect={handleSelectVideoPromptHistory}
                    title="プロンプト履歴"
                  />
                </div>

              </div>
            </div>

            {/* モーショングラフィックス選択中は動画設定 (モデル・解像度・秒課金の見積もり・参照動画) を
                隠し、代わりに専用の設定を出す (計画 md §5.1・§5.3) */}
            {isMotionMode && (
              <MotionGraphicsSettingsSection
                settings={motionSettings}
                onSettingsChange={setMotionSettings}
                assetNotice={motionAssetNotice}
                disabled={promptDisabled}
                layerMaterial={motionLayerTarget ? {
                  name: layerMaterialDisplayName(motionLayerTarget.asset, motionLayerTarget.stack),
                  layerCount: motionLayerTarget.stack.layers.length,
                  lockedCount: motionLockedCount,
                } : null}
                pendingLayerMaterialName={motionReadiness.ok && !motionReadiness.target && motionReadiness.pendingAsset
                  ? (motionReadiness.pendingAsset.name?.trim() || 'SVG 素材')
                  : null}
                onOpenLayers={motionLayerTarget ? () => openLayerModal(motionLayerTarget.asset.id) : undefined}
                credits={motionCredits}
                unavailableMessage={motionUnavailableMessage}
                aspectNotice={motionAspectNotice}
              />
            )}
            {layerModalTarget && (
              <MotionLayerModal
                asset={layerModalTarget.asset}
                stack={layerModalTarget.stack}
                onClose={closeLayerModal}
              />
            )}

            {/* 動画生成設定アコーディオン（デフォルトクローズ） */}
            {!isMotionMode && (
            <div className={`accordion-section ${isPromptVideoSettingsOpen ? 'accordion-section-open' : ''}`}>
              <button
                type="button"
                className="accordion-header"
                onClick={() => setIsPromptVideoSettingsOpen(!isPromptVideoSettingsOpen)}
                aria-expanded={isPromptVideoSettingsOpen}
              >
                <span className="accordion-title">
                  <img src="/kindle_hold.svg" alt="" className="accordion-title-icon" />
                  <span>動画生成設定</span>
                  <span className="accordion-summary">{videoSettingsSummaryPrompt}</span>
                </span>
                <span className={`accordion-icon ${isPromptVideoSettingsOpen ? 'accordion-icon-open' : ''}`}>
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" style={{ transition: 'transform 0.2s', transform: isPromptVideoSettingsOpen ? 'rotate(90deg)' : 'none' }}><path d="M2 0l4 4-4 4z" /></svg>
                </span>
              </button>
              <div className={`accordion-panel ${isPromptVideoSettingsOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
                <VideoGenerationSettings
                  settings={videoSettings}
                  onSettingsChange={setVideoSettings}
                  disabled={promptDisabled}
                  showResizeMode={videoGenerationMode === 'frame'}
                  isReferenceImageMode={isReferenceImageMode}
                  requireReferenceSupport={isReferenceImageMode}
                  referenceModeLabel="AIおまかせ"
                  hasReferenceVideo={!!refVideoAsset}
                  referenceVideoDurationSeconds={(() => {
                    // 複数の参照動画があるときは最長の尺を初期値の基準にする
                    const durations = selectedAssets
                      .filter(isVideoModeRefAsset)
                      .map((asset) => asset.durationSeconds)
                      .filter((d): d is number => typeof d === 'number' && d > 0);
                    return durations.length > 0 ? Math.max(...durations) : null;
                  })()}
                  // 解像度/アスペクト比は代表 (先頭) の参照動画に連動させる
                  referenceVideoWidth={refVideoAsset?.width ?? null}
                  referenceVideoHeight={refVideoAsset?.height ?? null}
                  referenceVideoAspectRatio={refVideoAsset?.aspectRatio ?? null}
                />
              </div>
            </div>
            )}

            {/* 参照動画の尺が消費ポイントに含まれる旨の注記 (合算課金モデルのみ。計画書 §4.1/P2)。
                想定外消費の防止が目的なので、既存の注記類 (8秒固定・不足時の案内) と同じ
                控えめなトーンに合わせる。2026-08-07 ユーザー決定: 非整数秒の素材は拒否せず
                切り上げて課金するため、切り上げが発生している場合のみその旨を併記する
                (「生成できない」という警告は廃止)。 */}
            {!isMotionMode && aggregatesReferenceVideoSeconds && referenceVideoSecondsTotal > 0 && (
              <div className={styles.generateGuidance}>
                参照動画の尺 ({referenceVideoSecondsTotal}秒分{hasCeiledReferenceVideoDuration ? '・小数秒は切り上げ' : ''}) が消費ポイントに含まれます。
              </div>
            )}

            {/* 動画生成ボタンと推定ポイント。尺「自動」(-1) は最大尺みなしの見積りの
                ため「~」を付けて上限であることを示す (実際の消費は生成された秒数で精算)。 */}
            {isMotionMode ? (
              <>
                {/* 定額のジョブ (POST /api/jobs/motion-layered/generate・計画 md §5.4)。押せない理由は
                    checkMotionLayeredReadiness (使えない・素材・動きの指示・ストレージ・残高) の 1 つだけ出す。
                    使えない理由は設定欄の料金の行に出しているのでここでは重ねない */}
                <GenerateButton
                  // 「再生成」判定の promptVideoGenerated は通常の動画と共用なので、motion は常に同じ文言にする
                  label="モーショングラフィックスを生成"
                  creditCost={motionCredits ?? undefined}
                  onClick={() => { void motionLayeredPrompt.generate(); }}
                  isLoading={motionLayeredPrompt.submitting}
                  loadingLabel="受け付けています…"
                  disabled={promptDisabled || motionLayeredPrompt.submitting || !motionReadiness.ok}
                  data-testid="motion-layered-generate"
                />
                {!motionReadiness.ok && motionReadiness.reason !== 'unavailable' && motionReadiness.reason !== 'asset' && (
                  <div className={styles.generateGuidance} data-testid="motion-layered-generate-guidance">
                    {motionReadiness.reason === 'credits' ? (
                      <div>ポイントが不足しています。<button type="button" onClick={openPurchaseModal} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>ポイントを購入</button>してください。</div>
                    ) : motionReadiness.reason === 'storage' ? (
                      <div>ストレージ容量の上限に達しました。<a href="/trash">ごみ箱</a>を空にすると空き容量を確保できます。プランのアップグレードで容量を引き上げることも可能です。<a href="/subscription">プランを確認</a></div>
                    ) : (
                      <div>{motionReadiness.message}</div>
                    )}
                  </div>
                )}
              </>
            ) : (
            <>
            <GenerateButton
              label={promptVideoGenerated ? '動画を再生成' : '動画を生成'}
              creditCost={videoSettings.duration === -1 ? `~${estimatedVideoCredits}` : estimatedVideoCredits}
              onClick={handleGenerateVideoFromPrompt}
              isLoading={videoGenerationMutation.isPending}
              loadingLabel="生成中…"
              // motion を選んだまま availability の取得中 (タブの表示を決めかねている間) は押させない
              // (押すと motion のハンドラに入って「使えない」トーストになり紛らわしい)
              disabled={promptDisabled || videoGenerationMutation.isPending || !videoPrompt.trim() || isVideoCreditInsufficient || isStorageExceeded || motionTabUndecided}
            />
            {videoSettings.duration === -1 && (
              <div className={styles.generateGuidance}>
                これは{assumedMaxDurationSeconds}秒の場合の消費ポイントです。実際に生成された秒数に応じて消費されます。
              </div>
            )}
            {(!videoPrompt.trim() || isVideoCreditInsufficient || isStorageExceeded) && (
              <div className={styles.generateGuidance}>
                {!videoPrompt.trim() && <div>プロンプトを入力してください。</div>}
                {isVideoCreditInsufficient && (
                  <div>ポイントが不足しています。<button type="button" onClick={openPurchaseModal} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>ポイントを購入</button>してください。</div>
                )}
                {isStorageExceeded && (
                  <div>ストレージ容量の上限に達しました。<a href="/trash">ごみ箱</a>を空にすると空き容量を確保できます。プランのアップグレードで容量を引き上げることも可能です。<a href="/subscription">プランを確認</a></div>
                )}
              </div>
            )}
            </>
            )}

            {/* エラーメッセージ表示。SEカタログ由来の利用不可 (§2.7) は下の確認ダイアログが
                専用の文言と選択肢を出すため、ここでは重複表示しない。 */}
            {videoGenerationMutation.isError && !catalogAudioUnavailable && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: 'rgba(220, 38, 38, 0.1)',
                border: '1px solid rgba(220, 38, 38, 0.3)',
                borderRadius: '4px',
                color: '#f87171',
                fontSize: '0.875rem',
                lineHeight: '1.5',
              }}>
                {resolveErrorMessage(videoGenerationMutation.error)}
              </div>
            )}

            {/* SEカタログ由来の参照音声が提供終了/削除されていた場合の確認 (§2.7)。
                無言で除いて続行はせず、明示的な承認を必須にする。 */}
            <ConfirmDialog
              isOpen={showCatalogAudioDialog}
              title="この参照音声は利用できません"
              message={
                <>
                  {catalogAudioUnavailable?.message}
                  <br />
                  この音声を除いて生成を続けますか？
                </>
              }
              messageAlign="left"
              confirmLabel="この音声なしで続行する"
              cancelLabel="キャンセル"
              variant="warning"
              onConfirm={handleCatalogAudioUnavailableRetry}
              onCancel={handleCatalogAudioUnavailableCancel}
            />

            {/* 生成された動画の表示 */}
            {promptVideoGenerated && promptVideoMaterialId && (
              <div ref={promptVideoDisplayRef}>
                <PromptVideoDisplay
                  materialId={promptVideoMaterialId}
                  tempMaterials={tempMaterials}
                  cloudMaterials={cloudMaterials}
                  onOpenPreview={setPreviewModalMaterial}
                />
              </div>
            )}

            {/* 動画生成履歴 */}
            <VideoJobHistoryPanel
              onPreview={handleVideoHistoryPreview}
              onRestore={handleRestoreVideoSettings}
            />
    </>
  );
}
